import { Action } from 'redux';
import _ from 'underscore';

import { ContentFavoriteAdded, ContentFavoriteRemoved, RecommendationHidden } from '~src/bootstrap/redux/actions/AppActionTypes';
import { isSetCollectionVisibility } from '~src/Collections/actions/SetCollectionVisibilityAction';
import { isAuthorConfiguredViewSucceededAction } from '~src/dataChangeRadar/actions/AuthorConfiguredViewsActions';
import {
  isSpaceContentsGetMonitoredViewStatusAction,
  isSpaceContentsGetMonitoredViewStatusFailedAction,
  isSpaceContentsGetMonitoredViewStatusSucceededAction,
} from '~src/dataChangeRadar/actions/DataChangeRadarActions';
import { IProcessedViewMonitoringStatusInfo, ProcessedMonitoringStatus } from '~src/dataChangeRadar/types/AuthorConfiguredDCRTypes';
import { isRemoveDraftFromContentInfoAction } from '~src/drafts/actions/DiscardDraftAction';
import { isDraftsInfoAvailable } from '~src/drafts/actions/DraftsActions';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { WorkbookDraftContentType } from '~src/drafts/types/DraftsTypes';
import { isContentFavoriteAddedAction, isContentFavoriteRemovedAction } from '~src/favorites/actions/FavoritesActions';
import {
  isRecommendationHiddenAction,
  isRecommendationUnhiddenAction
} from '~src/recommendations/actions/HideRecommendationAction';
import { ExplorableItemType, isRecommendationType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { IExplorableItemTypeInfo, IFavoriteItem } from '~src/spacesPlatform/models/resourceInfoParts';
import { isFavoritesSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { IViewInfo } from '~src/views/models/IViewInfo';

import { isUpdateContentDetailAction } from '../../actions/GetContentDetailAction';
import { isReorderItemAction } from '../../actions/ReorderItem';
import {
  isSpaceContentsNextPageWithTokenSuccededAction,
  isSpaceContentsRequestFailedAction,
  isSpaceContentsRequestSucceededAction,
  isSpaceContentsSliceSucceededAction,
  isSpaceContentsUpdateOwnersAction,
  isSpaceContentsWithTokenRequestSucceededAction
} from '../../actions/RequestSpaceContentsActions';

function findIndexOfContentById(content: ReadonlyArray<IExplorableItemTypeInfo>, type: string, id: string): number {
  return _.findIndex(content, c => c?.contentType === type && c.id === id);
}

function findIndexOfContentByLuid(content: ReadonlyArray<IExplorableItemTypeInfo>, type: string, id: string): number {
  return _.findIndex(content, c => c?.contentType === type && c.luid === id);
}

function updateContent(content: ReadonlyArray<IExplorableItemTypeInfo>,
                       type: ExplorableItemType,
                       id: string,
                       updateOptions: any,
                       isLuid: boolean = false,
): ReadonlyArray<IExplorableItemTypeInfo> {
  const index = isLuid ? findIndexOfContentByLuid(content, type, id) : findIndexOfContentById(content, type, id);
  if (index > -1) {
    const newContent = content.slice(0);
    newContent[index] = {
      ...content[index],
      ...updateOptions
    };
    return newContent;
  }
  return content;
}


export function contentReducer(
  previousState: ReadonlyArray<IExplorableItemTypeInfo> = [],
  action: Action = null
): ReadonlyArray<IExplorableItemTypeInfo> {
  if (isSpaceContentsRequestSucceededAction(action) && action.content) {
    // Don't set a new empty array as that will trigger renders.
    if (action.content.items.length === 0 && previousState.length === 0) {
      return previousState;
    }

    // We ignore any items in previous state and begin anew
    // We assume the slice starts with zero index so just extend
    // items array to match the total
    const emptyMissingItems =
      new Array(action.content.total - action.content.items.length);
    return action.content.items.concat(emptyMissingItems);

  } else if (isSpaceContentsSliceSucceededAction(action) && action.content) {
    if (previousState.length === action.content.total) {
      // if total items hasn't changed insert  newly retrieved items
      // into our existing items
      const items = previousState.slice();
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    } else {
      // Total number of items doesn't match our state total
      // So we start over and insert our slice
      const items = new Array(action.content.total);
      items.splice(action.start, action.content.items.length, ...action.content.items);
      return items;
    }

  } else if (isSpaceContentsWithTokenRequestSucceededAction(action) && action.contentWithToken) {
    // Don't set a new empty array as that will trigger renders.
    if (action.contentWithToken.items.length === 0 && previousState.length === 0) {
      return previousState;
    }
    return action.contentWithToken.items;
  } else if (isSpaceContentsNextPageWithTokenSuccededAction(action) && action.contentWithToken) {

    return previousState.concat(action.contentWithToken.items);
  } else if (isContentFavoriteAddedAction(action) || isContentFavoriteRemovedAction(action)) {
    // if in Recommendation space page, keep favorite states of RecommendationCards updated
    if (previousState.length > 0 && isRecommendationType(previousState[0].contentType)) {
      let content = _.find(previousState, c => c.id === action.item.id);
      if (action.type === ContentFavoriteAdded && !(content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal += 1;
      } else if (action.type === ContentFavoriteRemoved && (content as IContentInfo).favorite) {
        (content as IViewInfo).usageInfo.favoritesTotal -= 1;
      }

      return updateContent(previousState, 'recommendedView', action.item.id, {
        favorite: action.type === ContentFavoriteAdded,
      });
    }

    return updateContent(previousState, action.item.contentType, action.item.id, {
      favorite: action.type === ContentFavoriteAdded
    });
  } else if (isRemoveDraftFromContentInfoAction(action)) {
    let contentId = action.contentId.toString();
    const isLuidFlag: boolean = true;
    return updateContent(
      previousState,
      action.contentType,
      contentId,
      {
        draftLuid: null, draftStatus: null
      },
      isLuidFlag
    );
  } else if (isRecommendationHiddenAction(action) || isRecommendationUnhiddenAction(action)) {
    let contentId = action.contentId;
    let contentType = action.contentType;

    return updateContent(previousState, contentType, contentId, {
      hide: action.type === RecommendationHidden
    });

  } else if (isUpdateContentDetailAction(action)) {
    let updatedContent = previousState;
    for(let content of action.content) {
      updatedContent = updateContent(updatedContent, content.contentType, content.id, {...content});
    }
    return updatedContent;
  } else if (isSetCollectionVisibility(action)) {
    let updatedContent = previousState;
    for (let contentId of action.contentIds) {
      updatedContent = updateContent(updatedContent, 'collection', contentId, {
        visibility: action.visibility
      });
    }
    return updatedContent;
  } else if (isSpaceContentsUpdateOwnersAction(action)) {
      let updatedContent = previousState;
      for(let content of action.contents) {
        const updatedOwner = {owner: action.owners[content.ownerId]};
        updatedContent = updateContent(updatedContent, content.contentType, content.contentId, {...updatedOwner});
      }
      return updatedContent;
  } else if (isDraftsInfoAvailable(action)) {
    let updatedContent = previousState;
    const isLuidFlag: boolean = true;
    for (const draft of action.contents) {
      if (draft.draftStatus === ContentDraftStatus.unpublishedchanges) {
        updatedContent = updateContent(
          updatedContent,
          WorkbookDraftContentType,
          draft.parentId,
          {
            draftLuid: draft.draftLuid,
            draftStatus: draft.draftStatus,
          },
          isLuidFlag,
        );
      } else if (draft.draftStatus === ContentDraftStatus.unpublished) {
        updatedContent = updatedContent.concat(draft);
      }
    }
    return updatedContent;
  } else if (isReorderItemAction(action)) {
    let items: Array<IExplorableItemTypeInfo> = [];

    if (isFavoritesSpace(action.spaceInfo)) {
      // In the favorite space, we need to filter out non-favorited items. This happens when a user unfavorites an item
      // from the favorites space-- we keep it in the store in case they want to undo that action. To make sure the
      // reordering works correctly, we only want to operate on items that are showing in the ui (favorited items)
      previousState.forEach(item => {
        if((item as unknown as IFavoriteItem).favorite) {
          items.push(item);
        }
      });

    } else {
      items = previousState.slice(0);
    }

    const toIndex = Math.max(0, action.indexToMoveAfter + 1);
    if (action.fromIndex > toIndex) {
      items.splice(toIndex, 0, items.splice(action.fromIndex, 1)[0]);
    } else if (action.fromIndex < toIndex) {
      items.splice(toIndex, 0, items[action.fromIndex]);
      items.splice(action.fromIndex, 1);
    }
    return items;
  } else if (isSpaceContentsGetMonitoredViewStatusAction(action)) {
    const viewLuidsSet = new Set(action.viewLuids);
    return previousState.map(item => viewLuidsSet.has(item.luid) ? ({
      ...item,
      [OrderKeys.monitorDataChangesStatus]: ProcessedMonitoringStatus.Loading
    }) : item);
  } else if (isSpaceContentsGetMonitoredViewStatusFailedAction(action)) {
    const viewLuidsSet = new Set(action.viewLuids);
    return previousState.map(item => viewLuidsSet.has(item.luid) ? ({
      ...item,
      [OrderKeys.monitorDataChangesStatus]: ProcessedMonitoringStatus.Unknown
    }) : item);
  } else if (isSpaceContentsGetMonitoredViewStatusSucceededAction(action)) {
    const viewLuidsRequestedSet = new Set(action.viewLuidsRequested);
    const viewLuidToMonitoringStatusInfoMap = new Map<string, IProcessedViewMonitoringStatusInfo>();
    for (const viewMonitoringStatusInfo of action.viewMonitoringStatusesInfo) {
      viewLuidToMonitoringStatusInfoMap.set(viewMonitoringStatusInfo.viewLuid, viewMonitoringStatusInfo);
    }
    return previousState.map(item => {
      if (viewLuidToMonitoringStatusInfoMap.has(item.luid)) {
        return {
          ...item,
          [OrderKeys.monitorDataChangesStatus]: viewLuidToMonitoringStatusInfoMap.get(item.luid).status,
          [OrderKeys.monitorDataChangesAuthorSelection]: viewLuidToMonitoringStatusInfoMap.get(item.luid).shouldMonitor
        };
      } else if (viewLuidsRequestedSet.has(item.luid)) {
        // Update to unknown for the views requested but no response
        const newState = {
          ...item,
          [OrderKeys.monitorDataChangesStatus]:
            ProcessedMonitoringStatus.Unknown,
        };

        // Casting is bad practice, but we have to cast to 'any' here to delete 
        // since the current type of item state doesn’t include DCR attributes
        delete (newState as any)[OrderKeys.monitorDataChangesAuthorSelection];
        return newState;
      }
      return item;
    });
  } else if (isAuthorConfiguredViewSucceededAction(action)) {
    return previousState.map(item => {
      if (item.luid === action.viewMonitoringStatusInfo.viewLuid) {
        return {
          ...item,
          [OrderKeys.monitorDataChangesStatus]: action.viewMonitoringStatusInfo.status,
          [OrderKeys.monitorDataChangesAuthorSelection]: action.viewMonitoringStatusInfo.shouldMonitor
        };
      }
      return item;
    });
  } else if (isSpaceContentsRequestFailedAction(action)) {
    // Don't set a new empty array as that will trigger renders.
    if (previousState.length === 0) {
      return previousState;
    }

    return [];
  }

  return previousState;
}
